/**
 * RoadDisplayField — self-contained, server-backed road selection control.
 *
 * Built on Mantine `Combobox` primitives: a free-text `TextInput` target plus a
 * portaled, scrollable options dropdown with infinite scroll. Suggestions are
 * fetched internally via `useRoadSuggestions`; the input value stays arbitrary
 * free text (the `location` string). Selecting an option sets the value to the
 * formatted display name. GPS detection and status affordances are preserved.
 */

import { useState } from 'react';
import { Alert, Combobox, TextInput, useCombobox } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconRoad, IconAlertCircle } from '@tabler/icons-react';
import { useRoadSuggestions } from '@/hooks/useRoadSuggestions';
import { formatRoadDisplayName } from '@/utils/roads';
import { InfiniteScrollSection } from '@/components/ui/PaginatedList';
import { RoadFieldLabel } from './parts/RoadFieldLabel';
import { GpsActionButton } from './parts/GpsActionButton';
import { RoadOption } from './parts/RoadOption';
import { RoadDropdownState } from './parts/RoadDropdownState';
import type { RoadDisplayFieldProps } from './RoadDisplayField.types';
import { ROAD_DISPLAY_FIELD_CONSTANTS } from './RoadDisplayField.constants';
import classes from './RoadDisplayField.module.css';

export function RoadDisplayField({
  value,
  onChange,
  isDetectingGPS = false,
  isWaitingForInitialGPS = false,
  gpsError = null,
  roadSource,
  disabled = false,
  error,
  onRequestGPSDetection,
  onManualRoadInput,
}: RoadDisplayFieldProps) {
  const { t } = useTranslation();

  // DOM node of the scrollable options list — used as the IntersectionObserver
  // root so infinite-scroll fires on the dropdown's own scroll, not the page.
  const [optionsElement, setOptionsElement] = useState<HTMLDivElement | null>(
    null
  );

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // The field is ACTIVELY being searched exactly while the dropdown is open
  // (focus / click / typing open it; option-submit / blur close it). Suggestions
  // are queried only while open — otherwise `value` holds a formatted display
  // name (after option-select or GPS) the roads endpoint can't match, which
  // would fire a wasted, zero-result request and show "no results" for the
  // user's own selection on reopen.
  const {
    items,
    isLoading,
    hasNextPage,
    loadMore,
    error: hasFetchError,
  } = useRoadSuggestions(value, combobox.dropdownOpened);

  function beginSearching() {
    combobox.openDropdown();
  }

  function endSearching() {
    combobox.closeDropdown();
  }

  // Focus/click opens suggestions only for an EMPTY field (browse from scratch).
  // When the field already holds a value — a GPS suggestion or a prior
  // selection — it is a formatted display name the roads endpoint cannot match,
  // so opening would show a spurious "no roads found". Typing resumes the search
  // via handleInputChange once the user actively edits the field.
  function handleFieldActivate() {
    if (!value) {
      beginSearching();
    }
  }

  const isControlDisabled =
    disabled || isDetectingGPS || isWaitingForInitialGPS;

  function emitChange(nextValue: string) {
    if (onManualRoadInput) {
      onManualRoadInput(nextValue);
    } else {
      onChange(nextValue);
    }
  }

  function handleInputChange(nextValue: string) {
    emitChange(nextValue);
    beginSearching();
    combobox.updateSelectedOptionIndex();
  }

  // Selecting a suggestion is a MANUAL action — route it through the manual
  // input path so `roadSource` becomes 'manual' (clears any stale GPS/map badge).
  // It also ENDS the active search so we don't re-query with the formatted
  // display name (which the roads endpoint can't match).
  function handleOptionSubmit(selectedRef: string) {
    const selected = items.find((suggestion) => suggestion.ref === selectedRef);
    if (selected) {
      emitChange(formatRoadDisplayName(selected.ref, selected.roadType, t));
    }
    endSearching();
  }

  function handleGPSDetection() {
    if (onRequestGPSDetection) {
      onRequestGPSDetection();
    }
  }

  const hasOptions = items.length > 0;

  return (
    <div className={classes.container}>
      <Combobox
        store={combobox}
        withinPortal
        zIndex={ROAD_DISPLAY_FIELD_CONSTANTS.DROPDOWN_Z_INDEX}
        onOptionSubmit={handleOptionSubmit}
      >
        <Combobox.Target>
          <TextInput
            className={classes.roadField}
            label={
              <RoadFieldLabel
                isDetectingGPS={isDetectingGPS}
                isWaitingForInitialGPS={isWaitingForInitialGPS}
                value={value}
                roadSource={roadSource ?? null}
              />
            }
            leftSection={
              <IconRoad size={ROAD_DISPLAY_FIELD_CONSTANTS.ICONS.ROAD} />
            }
            rightSection={
              <GpsActionButton
                value={value}
                isDetectingGPS={isDetectingGPS}
                disabled={disabled}
                onRequestGPSDetection={handleGPSDetection}
              />
            }
            placeholder={t('taskForm.locationPlaceholder')}
            value={value}
            onChange={(event) => handleInputChange(event.currentTarget.value)}
            onFocus={handleFieldActivate}
            onClick={handleFieldActivate}
            onBlur={endSearching}
            disabled={isControlDisabled}
            error={error}
            autoComplete="off"
            classNames={{ input: classes.input }}
          />
        </Combobox.Target>

        <Combobox.Dropdown className={classes.dropdown}>
          <Combobox.Options
            ref={setOptionsElement}
            mah={ROAD_DISPLAY_FIELD_CONSTANTS.MAX_DROPDOWN_HEIGHT}
            className={classes.options}
          >
            {hasOptions ? (
              <>
                {items.map((suggestion) => (
                  <Combobox.Option key={suggestion.ref} value={suggestion.ref}>
                    <RoadOption suggestion={suggestion} />
                  </Combobox.Option>
                ))}
                <InfiniteScrollSection
                  hasNextPage={hasNextPage}
                  isLoading={isLoading}
                  onLoadMore={loadMore}
                  threshold={ROAD_DISPLAY_FIELD_CONSTANTS.LOAD_MORE_THRESHOLD}
                  root={optionsElement}
                />
              </>
            ) : (
              <RoadDropdownState
                isLoading={isLoading}
                isError={hasFetchError}
              />
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      {gpsError && (
        <Alert
          icon={
            <IconAlertCircle size={ROAD_DISPLAY_FIELD_CONSTANTS.ICONS.ROAD} />
          }
          color="red"
          variant="light"
          className={classes.errorAlert}
        >
          {gpsError}
        </Alert>
      )}
    </div>
  );
}
